import { useEffect, useState } from 'react';
import JWModal from '../brand/JWModal';
import DisplayNameField from '../Profile/DisplayNameField';
import { FormGroup } from 'reactstrap';
import { useSelector } from 'react-redux';
import CityLookupField from '../Profile/CityLookup/CityLookupField';
import JWButton from '../brand/JWButton';
import { useNavigate } from 'react-router-dom';
import SocialMediaInput from '../inputs/SocialMediaInput';
import { citySelectionFromText, emptyCityPlace } from '../../utils/cityPlace';
import { PRIMARY_BUTTON_CLASS } from '../../pages/Onboarding/OnboardingUI';
import {
    validateDisplayName,
    validateUrl,
    validateSocialMediaHandle,
    validateSpotifyArtistUrl,
    validateAppleMusicArtistUrl,
    validateSoundCloudUrl,
    validateBandcampUrl,
    validateDiscordUrl,
    validateTwitchUrl,
    validateXUrl,
    validateLinkedInUrl,
    validateThreadsUrl,
    validateNewgroundsUrl,
    isValidUrl
} from '../../utils/validation';

import {
    FacebookIcon,
    InstagramIcon,
    YoutubeIcon,
    SoundcloudIconSVG,
    SpotifyIcon,
    AppleMusicIconSVG,
    BandcampIconSVG,
    GlobeIcon,
    SnapchatIcon,
    TikTokIcon,
    DiscordIcon,
    TwitchIcon,
    XLogoIcon,
    LinkedInIcon,
    ThreadsIcon,
    NewgroundsIcon,
} from '../../components/icons';

type EditProfileModalProps = {
    profileInfo: any
    userInfo: any
    onProfileSave: any
    handleInputChange: any
    show: boolean
    onClose: any
    setShow: any
}

function normalizeSocialMediaUrl(baseUrl: string, userInput: string): string {
    if (!userInput) return '';

    // Remove any leading/trailing whitespace
    userInput = userInput.trim();

    // If it's already a full URL, return it
    if (userInput.startsWith('http://') || userInput.startsWith('https://')) {
        return userInput;
    }

    // If it's already the base URL, return it
    if (userInput.startsWith(baseUrl)) {
        return userInput;
    }

    // Remove @ symbol if present
    if (userInput.startsWith('@')) {
        userInput = userInput.substring(1);
    }

    // Remove any platform-specific prefixes that might be present
    if (baseUrl.includes('instagram.com')) {
        userInput = userInput.replace('instagram.com/', '');
    } else if (baseUrl.includes('facebook.com')) {
        userInput = userInput.replace('facebook.com/', '');
    } else if (baseUrl.includes('spotify.com')) {
        userInput = userInput.replace('spotify.com/artist/', '');
    } else if (baseUrl.includes('soundcloud.com')) {
        userInput = userInput.replace('soundcloud.com/', '');
    } else if (baseUrl.includes('music.apple.com')) {
        userInput = userInput.replace('music.apple.com/artist/', '');
    } else if (baseUrl.includes('youtube.com')) {
        userInput = userInput.replace('youtube.com/@', '');
        userInput = userInput.replace('youtube.com/channel/', '');
    } else if (baseUrl.includes('bandcamp.com')) {
        userInput = userInput.replace('bandcamp.com/', '');
    } else if (baseUrl.includes('snapchat.com')) {
        userInput = userInput.replace('snapchat.com/add/', '');
    } else if (baseUrl.includes('tiktok.com')) {
        userInput = userInput.replace('tiktok.com/@', '');
    }

    return `${baseUrl}${userInput}`;
}

function EditProfileModal(props: EditProfileModalProps) {
    const {
        profileInfo,
        userInfo,
        onProfileSave,
        handleInputChange,
        show,
        onClose,
        setShow
    } = props;

    const [locationInputText, setLocationInputText] = useState<string>('');
    const [isEditingLocationInputText, setIsEditingLocationInputText] = useState<boolean>(false);
    const [profileCopy, setProfileCopy] = useState<any>(profileInfo);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

    // Track original profile for comparison
    const [originalProfile, setOriginalProfile] = useState<any>(profileInfo);

    const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayNameTouched, setDisplayNameTouched] = useState(false);

    // Update profileCopy when profileInfo changes (from props), but only on initial mount
    useEffect(() => {
        // Only update if profileInfo exists and profileCopy hasn't been initialized yet
        if (profileInfo && JSON.stringify(profileCopy) === JSON.stringify(originalProfile)) {
            setProfileCopy(profileInfo);
            setOriginalProfile(profileInfo);
        }
    }, [profileInfo]);

    // Store changes locally
    const updateLocalState = (updatedFields: any) => {
        setProfileCopy({
            ...profileCopy,
            ...updatedFields
        });
    };

    const clearFieldError = (field: string) => {
        setFormErrors(prev => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    // Track changes by comparing current state with original profile info
    useEffect(() => {
        // Check if profileInfo is available
        if (!originalProfile) {
            setHasUnsavedChanges(false);
            return;
        }

        // Create a set of all keys from both objects to compare
        const allKeys = new Set([
            ...Object.keys(profileCopy || {}),
            ...Object.keys(originalProfile || {})
        ]);

        // Check for differences in any key
        const hasChanges = Array.from(allKeys).some(key => {
            // Special handling for objects like location
            if (typeof profileCopy[key] === 'object' && profileCopy[key] !== null &&
                typeof originalProfile[key] === 'object' && originalProfile[key] !== null) {
                return JSON.stringify(profileCopy[key]) !== JSON.stringify(originalProfile[key]);
            }

            // Simple comparison for primitive values
            return profileCopy[key] !== originalProfile[key];
        });

        setHasUnsavedChanges(hasChanges);
    }, [profileCopy, originalProfile]);

    // Handle ESC key press
    useEffect(() => {
        const handleEscKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && show) {
                handleCloseModal();
            }
        };

        // Add event listener when modal is open
        if (show) {
            window.addEventListener('keydown', handleEscKeyPress);
        }

        // Clean up
        return () => {
            window.removeEventListener('keydown', handleEscKeyPress);
        };
    }, [show, hasUnsavedChanges]);

    const handleCloseModal = () => {
        if (hasUnsavedChanges) {
            setShowConfirmDialog(true);
        } else {
            onClose();
        }
    };

    const handleConfirmClose = () => {
        setShowConfirmDialog(false);
        // Reset profileCopy to original profileInfo to discard changes
        setProfileCopy(originalProfile);
        onClose();
        // Refresh the page
        window.location.reload();
    };

    const handleCancelClose = () => {
        setShowConfirmDialog(false);
    };

    // Save all changes to parent component
    const saveChangesToParent = () => {
        // Not needed anymore since changes are saved immediately in onBlur events
    };

    const handleSaveProfile = async () => {
        // Validate all fields before saving
        const errors: {[key: string]: string} = {};

        // Always validate display name on save
        const displayNameValidation = validateDisplayName(profileCopy.displayName || '');
        if (!displayNameValidation.isValid) {
            errors.displayName = displayNameValidation.error!;
        }

        // Validate social media fields
        if (profileCopy.instagram) {
            const instagramValidation = validateSocialMediaHandle(profileCopy.instagram, 'instagram');
            if (!instagramValidation.isValid) {
                errors.instagram = instagramValidation.error!;
            }
        }

        if (profileCopy.facebook) {
            const facebookValidation = validateSocialMediaHandle(profileCopy.facebook, 'facebook');
            if (!facebookValidation.isValid) {
                errors.facebook = facebookValidation.error!;
            }
        }

        if (profileCopy.youtube) {
            const youtubeValidation = validateSocialMediaHandle(profileCopy.youtube, 'youtube');
            if (!youtubeValidation.isValid) {
                errors.youtube = youtubeValidation.error!;
            }
        }

        if (profileCopy.tiktok) {
            const tiktokValidation = validateSocialMediaHandle(profileCopy.tiktok, 'tiktok');
            if (!tiktokValidation.isValid) {
                errors.tiktok = tiktokValidation.error!;
            }
        }

        if (profileCopy.snapchat) {
            const snapchatValidation = validateSocialMediaHandle(profileCopy.snapchat, 'snapchat');
            if (!snapchatValidation.isValid) {
                errors.snapchat = snapchatValidation.error!;
            }
        }

        if (profileCopy.discord) {
            const discordValidation = validateDiscordUrl(profileCopy.discord);
            if (!discordValidation.isValid) {
                errors.discord = discordValidation.error!;
            }
        }

        if (profileCopy.twitch) {
            const twitchValidation = validateTwitchUrl(profileCopy.twitch);
            if (!twitchValidation.isValid) {
                errors.twitch = twitchValidation.error!;
            }
        }

        if (profileCopy.x) {
            const xValidation = validateXUrl(profileCopy.x);
            if (!xValidation.isValid) {
                errors.x = xValidation.error!;
            }
        }

        if (profileCopy.linkedin) {
            const linkedinValidation = validateLinkedInUrl(profileCopy.linkedin);
            if (!linkedinValidation.isValid) {
                errors.linkedin = linkedinValidation.error!;
            }
        }

        if (profileCopy.threads) {
            const threadsValidation = validateThreadsUrl(profileCopy.threads);
            if (!threadsValidation.isValid) {
                errors.threads = threadsValidation.error!;
            }
        }

        if (profileCopy.newgrounds) {
            const newgroundsValidation = validateNewgroundsUrl(profileCopy.newgrounds);
            if (!newgroundsValidation.isValid) {
                errors.newgrounds = newgroundsValidation.error!;
            }
        }

        if (profileCopy.spotify) {
            const spotifyValidation = validateSpotifyArtistUrl(profileCopy.spotify);
            if (!spotifyValidation.isValid) {
                errors.spotify = spotifyValidation.error!;
            }
        }

        if (profileCopy.applemusic) {
            const appleMusicValidation = validateAppleMusicArtistUrl(profileCopy.applemusic);
            if (!appleMusicValidation.isValid) {
                errors.applemusic = appleMusicValidation.error!;
            }
        }

        if (profileCopy.soundcloud) {
            const soundcloudValidation = validateSoundCloudUrl(profileCopy.soundcloud);
            if (!soundcloudValidation.isValid) {
                errors.soundcloud = soundcloudValidation.error!;
            }
        }

        if (profileCopy.bandcamp) {
            const bandcampValidation = validateBandcampUrl(profileCopy.bandcamp);
            if (!bandcampValidation.isValid) {
                errors.bandcamp = bandcampValidation.error!;
            }
        }

        if (profileCopy.personalUrl) {
            const websiteValidation = validateUrl(profileCopy.personalUrl);
            if (!websiteValidation.isValid) {
                errors.personalUrl = websiteValidation.error!;
            }
        }

        const shownCity = isEditingLocationInputText
            ? locationInputText
            : (profileCopy?.location?.displayText || '');
        const city = citySelectionFromText(shownCity, profileCopy?.location);
        if (!city.ok) {
            errors.location = city.error;
        }

        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            setShowConfirmDialog(false);
            return;
        }

        setIsSubmitting(true);
        try {
            await onProfileSave();
            setOriginalProfile({ ...profileCopy });
            setHasUnsavedChanges(false);
            setShowConfirmDialog(false);
            onClose();
        } catch (error) {
            console.error('Error saving profile:', error);
            setFormErrors({
                submit: 'Failed to save profile. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveAndClose = async () => {
        await handleSaveProfile();
    };

    const handleCityText = (value: string) => {
        setLocationInputText(value);
        setIsEditingLocationInputText(true);
        clearFieldError('location');
        if (!value.trim()) {
            const cleared = emptyCityPlace();
            updateLocalState({ location: cleared });
            handleInputChange({ location: cleared });
        }
    };

    const handleCitySelect = (place: { displayText: string }) => {
        updateLocalState({ location: place });
        setLocationInputText(place.displayText);
        setIsEditingLocationInputText(false);
        clearFieldError('location');
        handleInputChange({ location: place });
    };

    const handleCityBlur = () => {
        const shownCity = isEditingLocationInputText
            ? locationInputText
            : (profileCopy?.location?.displayText || '');
        const city = citySelectionFromText(shownCity, profileCopy?.location);
        if (!city.ok) {
            setFormErrors((prev) => ({ ...prev, location: city.error }));
        }
    };

    const handleClearAllSocials = () => {
        // Clear all social media fields
        const clearedFields = {
            instagram: '',
            facebook: '',
            youtube: '',
            tiktok: '',
            snapchat: '',
            spotify: '',
            applemusic: '',
            soundcloud: '',
            bandcamp: '',
            discord: '',
            twitch: '',
            x: '',
            linkedin: '',
            threads: '',
            newgrounds: '',
            personalUrl: ''
        };

        // Update local state
        updateLocalState(clearedFields);

        // Track all changes at once
        handleInputChange(clearedFields);

        // Clear any social media related errors
        setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.instagram;
            delete newErrors.facebook;
            delete newErrors.youtube;
            delete newErrors.tiktok;
            delete newErrors.snapchat;
            delete newErrors.spotify;
            delete newErrors.applemusic;
            delete newErrors.soundcloud;
            delete newErrors.bandcamp;
            delete newErrors.discord;
            delete newErrors.twitch;
            delete newErrors.x;
            delete newErrors.linkedin;
            delete newErrors.threads;
            delete newErrors.newgrounds;
            delete newErrors.personalUrl;
            return newErrors;
        });
    };

    const hasBlockingFieldErrors =
        Object.keys(formErrors).filter((k) => k !== 'submit').length > 0;

    return (
        <>
            <JWModal
                show={show}
                onClose={handleCloseModal}
                setShow={setShow}
                wrapperClassName={'edit-profile-modal md:w-[640px] w-full h-[80vh] min-h-0 overflow-hidden'}
                title="Edit Profile Info"
            >
                <FormGroup className="flex flex-1 min-h-0 flex-col overflow-hidden !mb-0 bg-transparent">
                    <div className="relative z-20 shrink-0 px-1 pb-2">
                        <div className="grid grid-cols-2 items-start gap-3">
                            <DisplayNameField
                                id="edit-profile-display-name"
                                value={profileCopy.displayName || (userInfo?.firstName && userInfo?.lastName ? `${userInfo.firstName} ${userInfo.lastName}` : '')}
                                error={formErrors.displayName}
                                onChange={(value) => {
                                    updateLocalState({ displayName: value });
                                    clearFieldError('displayName');
                                }}
                                onBlur={(value) => {
                                    setDisplayNameTouched(true);
                                    const validation = validateDisplayName(value);
                                    if (validation.isValid) {
                                        updateLocalState({ displayName: value });
                                        handleInputChange({ displayName: value });
                                    } else {
                                        setFormErrors((prev) => ({
                                            ...prev,
                                            displayName: validation.error!,
                                        }));
                                    }
                                }}
                            />
                            <CityLookupField
                                id="edit-profile-city"
                                label="City"
                                hint="Optional. Helps you find people nearby."
                                hintPlacement="inline"
                                placeholder="Start typing your city"
                                menuPlacement="below"
                                value={isEditingLocationInputText ? locationInputText || '' : (profileCopy?.location?.displayText || '')}
                                query={isEditingLocationInputText ? locationInputText : ''}
                                error={formErrors.location}
                                onTextChange={handleCityText}
                                onSelect={handleCitySelect}
                                onBlur={handleCityBlur}
                            />
                        </div>
                        <div className="sticky top-0 z-10 mt-2 flex items-center justify-between pb-1">
                            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">Links</h3>
                            <button
                                type="button"
                                onClick={handleClearAllSocials}
                                className="text-xs font-semibold text-gray-400 hover:text-purple-light"
                            >
                                Clear all
                            </button>
                        </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 pb-2">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<InstagramIcon />}
                                    value={profileCopy?.instagram || ''}
                                    placeholder="Instagram @ or link"
                                    onChange={(val) => {
                                        clearFieldError('instagram');
                                        updateLocalState({
                                            instagram: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        // Only normalize if it's not already a URL
                                        const normalizedValue = isValidUrl(val) ? val : normalizeSocialMediaUrl('https://www.instagram.com/', val);
                                        updateLocalState({ instagram: normalizedValue });
                                        handleInputChange({ instagram: normalizedValue });
                                    }}
                                    validate={(val) => validateSocialMediaHandle(val, 'instagram')}
                                    externalError={formErrors.instagram}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<TikTokIcon />}
                                    value={profileCopy?.tiktok || ''}
                                    placeholder="TikTok @ or link"
                                    onChange={(val) => {
                                        clearFieldError('tiktok');
                                        updateLocalState({
                                            tiktok: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let tiktokUrl = val.trim();

                                            // If empty, clear the field
                                            if (!tiktokUrl) {
                                                updateLocalState({ tiktok: '' });
                                                handleInputChange({ tiktok: '' });
                                                return;
                                            }

                                            // Normalize the URL
                                            if (!tiktokUrl.startsWith('http://') && !tiktokUrl.startsWith('https://')) {
                                                if (tiktokUrl.includes('tiktok.com/')) {
                                                    tiktokUrl = `https://${tiktokUrl}`;
                                                } else {
                                                    tiktokUrl = `https://www.tiktok.com/@${tiktokUrl.replace('@', '')}`;
                                                }
                                            }

                                            // Validate the URL
                                            const validation = validateSocialMediaHandle(tiktokUrl, 'tiktok');
                                            if (validation.isValid) {
                                                updateLocalState({ tiktok: tiktokUrl });
                                                handleInputChange({ tiktok: tiktokUrl });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    tiktok: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={(val) => validateSocialMediaHandle(val, 'tiktok')}
                                    externalError={formErrors.tiktok}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<SpotifyIcon />}
                                    value={profileCopy?.spotify || ''}
                                    placeholder="Spotify artist link"
                                    onChange={(val) => {
                                        clearFieldError('spotify');
                                        updateLocalState({
                                            spotify: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        updateLocalState({ spotify: val });
                                        handleInputChange({ spotify: val });
                                    }}
                                    validate={validateSpotifyArtistUrl}
                                    externalError={formErrors.spotify}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<AppleMusicIconSVG />}
                                    value={profileCopy?.applemusic || ''}
                                    placeholder="Apple Music artist link"
                                    onChange={(val) => {
                                        clearFieldError('applemusic');
                                        updateLocalState({
                                            applemusic: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        updateLocalState({ applemusic: val });
                                        handleInputChange({ applemusic: val });
                                    }}
                                    validate={validateAppleMusicArtistUrl}
                                    externalError={formErrors.applemusic}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<YoutubeIcon />}
                                    value={profileCopy?.youtube || ''}
                                    placeholder="YouTube @ or link"
                                    onChange={(val) => {
                                        clearFieldError('youtube');
                                        updateLocalState({
                                            youtube: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        const normalizedValue = isValidUrl(val) ? val : normalizeSocialMediaUrl('https://www.youtube.com/@', val);
                                        updateLocalState({ youtube: normalizedValue });
                                        handleInputChange({ youtube: normalizedValue });
                                    }}
                                    validate={(val) => validateSocialMediaHandle(val, 'youtube')}
                                    externalError={formErrors.youtube}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<SoundcloudIconSVG />}
                                    value={profileCopy?.soundcloud || ''}
                                    placeholder="SoundCloud link"
                                    onChange={(val) => {
                                        clearFieldError('soundcloud');
                                        updateLocalState({
                                            soundcloud: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        const normalizedValue = isValidUrl(val) ? val : normalizeSocialMediaUrl('https://soundcloud.com/', val);
                                        updateLocalState({ soundcloud: normalizedValue });
                                        handleInputChange({ soundcloud: normalizedValue });
                                    }}
                                    validate={validateSoundCloudUrl}
                                    externalError={formErrors.soundcloud}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<BandcampIconSVG />}
                                    value={profileCopy?.bandcamp || ''}
                                    placeholder="Bandcamp artist link"
                                    onChange={(val) => {
                                        clearFieldError('bandcamp');
                                        updateLocalState({
                                            bandcamp: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let bandcampUrl = val.trim();

                                            // If empty, clear the field
                                            if (!bandcampUrl) {
                                                updateLocalState({ bandcamp: '' });
                                                handleInputChange({ bandcamp: '' });
                                                return;
                                            }

                                            // Normalize the URL
                                            if (!bandcampUrl.startsWith('http://') && !bandcampUrl.startsWith('https://')) {
                                                if (bandcampUrl.includes('.bandcamp.com')) {
                                                    bandcampUrl = `https://${bandcampUrl}`;
                                                } else {
                                                    bandcampUrl = `https://bandcamp.com/${bandcampUrl}`;
                                                }
                                            }

                                            // Validate the URL
                                            const validation = validateBandcampUrl(bandcampUrl);
                                            if (validation.isValid) {
                                                updateLocalState({ bandcamp: bandcampUrl });
                                                handleInputChange({ bandcamp: bandcampUrl });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    bandcamp: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateBandcampUrl}
                                    externalError={formErrors.bandcamp}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<FacebookIcon />}
                                    value={profileCopy?.facebook || ''}
                                    placeholder="Facebook @ or link"
                                    onChange={(val) => {
                                        clearFieldError('facebook');
                                        updateLocalState({
                                            facebook: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        const normalizedValue = isValidUrl(val) ? val : normalizeSocialMediaUrl('https://www.facebook.com/', val);
                                        updateLocalState({ facebook: normalizedValue });
                                        handleInputChange({ facebook: normalizedValue });
                                    }}
                                    validate={(val) => validateSocialMediaHandle(val, 'facebook')}
                                    externalError={formErrors.facebook}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<LinkedInIcon />}
                                    value={profileCopy?.linkedin || ''}
                                    placeholder="LinkedIn profile link"
                                    onChange={(val) => {
                                        clearFieldError('linkedin');
                                        updateLocalState({ linkedin: val });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let u = val.trim();
                                            if (!u) {
                                                updateLocalState({ linkedin: '' });
                                                handleInputChange({ linkedin: '' });
                                                return;
                                            }
                                            if (!u.startsWith('http://') && !u.startsWith('https://')) {
                                                u = `https://${u}`;
                                            }
                                            const validation = validateLinkedInUrl(u);
                                            if (validation.isValid) {
                                                updateLocalState({ linkedin: u });
                                                handleInputChange({ linkedin: u });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    linkedin: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateLinkedInUrl}
                                    externalError={formErrors.linkedin}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<SnapchatIcon />}
                                    value={profileCopy?.snapchat || ''}
                                    placeholder="Snapchat @ or link"
                                    onChange={(val) => {
                                        clearFieldError('snapchat');
                                        updateLocalState({
                                            snapchat: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        const normalizedValue = isValidUrl(val) ? val : normalizeSocialMediaUrl('https://www.snapchat.com/add/', val);
                                        updateLocalState({ snapchat: normalizedValue });
                                        handleInputChange({ snapchat: normalizedValue });
                                    }}
                                    validate={(val) => validateSocialMediaHandle(val, 'snapchat')}
                                    externalError={formErrors.snapchat}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<XLogoIcon />}
                                    value={profileCopy?.x || ''}
                                    placeholder="X (Twitter) link"
                                    onChange={(val) => {
                                        clearFieldError('x');
                                        updateLocalState({ x: val });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let u = val.trim();
                                            if (!u) {
                                                updateLocalState({ x: '' });
                                                handleInputChange({ x: '' });
                                                return;
                                            }
                                            if (!u.startsWith('http://') && !u.startsWith('https://')) {
                                                u = `https://${u}`;
                                            }
                                            const validation = validateXUrl(u);
                                            if (validation.isValid) {
                                                updateLocalState({ x: u });
                                                handleInputChange({ x: u });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    x: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateXUrl}
                                    externalError={formErrors.x}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<ThreadsIcon />}
                                    value={profileCopy?.threads || ''}
                                    placeholder="Threads profile link"
                                    onChange={(val) => {
                                        clearFieldError('threads');
                                        updateLocalState({ threads: val });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let u = val.trim();
                                            if (!u) {
                                                updateLocalState({ threads: '' });
                                                handleInputChange({ threads: '' });
                                                return;
                                            }
                                            if (!u.startsWith('http://') && !u.startsWith('https://')) {
                                                u = `https://${u}`;
                                            }
                                            const validation = validateThreadsUrl(u);
                                            if (validation.isValid) {
                                                updateLocalState({ threads: u });
                                                handleInputChange({ threads: u });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    threads: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateThreadsUrl}
                                    externalError={formErrors.threads}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<TwitchIcon />}
                                    value={profileCopy?.twitch || ''}
                                    placeholder="Twitch channel link"
                                    onChange={(val) => {
                                        clearFieldError('twitch');
                                        updateLocalState({ twitch: val });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let u = val.trim();
                                            if (!u) {
                                                updateLocalState({ twitch: '' });
                                                handleInputChange({ twitch: '' });
                                                return;
                                            }
                                            if (!u.startsWith('http://') && !u.startsWith('https://')) {
                                                u = `https://${u}`;
                                            }
                                            const validation = validateTwitchUrl(u);
                                            if (validation.isValid) {
                                                updateLocalState({ twitch: u });
                                                handleInputChange({ twitch: u });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    twitch: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateTwitchUrl}
                                    externalError={formErrors.twitch}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<DiscordIcon />}
                                    value={profileCopy?.discord || ''}
                                    placeholder="Discord link"
                                    onChange={(val) => {
                                        clearFieldError('discord');
                                        updateLocalState({
                                            discord: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let discordUrl = val.trim();

                                            if (!discordUrl) {
                                                updateLocalState({ discord: '' });
                                                handleInputChange({ discord: '' });
                                                return;
                                            }

                                            if (!discordUrl.startsWith('http://') && !discordUrl.startsWith('https://')) {
                                                discordUrl = `https://${discordUrl}`;
                                            }

                                            const validation = validateDiscordUrl(discordUrl);
                                            if (validation.isValid) {
                                                updateLocalState({ discord: discordUrl });
                                                handleInputChange({ discord: discordUrl });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    discord: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateDiscordUrl}
                                    externalError={formErrors.discord}
                                />
                            </div>
                        </div>

                        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<NewgroundsIcon />}
                                    value={profileCopy?.newgrounds || ''}
                                    placeholder="Newgrounds profile link"
                                    onChange={(val) => {
                                        clearFieldError('newgrounds');
                                        updateLocalState({ newgrounds: val });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            let u = val.trim();
                                            if (!u) {
                                                updateLocalState({ newgrounds: '' });
                                                handleInputChange({ newgrounds: '' });
                                                return;
                                            }
                                            if (!u.startsWith('http://') && !u.startsWith('https://')) {
                                                u = `https://${u}`;
                                            }
                                            const validation = validateNewgroundsUrl(u);
                                            if (validation.isValid) {
                                                updateLocalState({ newgrounds: u });
                                                handleInputChange({ newgrounds: u });
                                            } else {
                                                setFormErrors({
                                                    ...formErrors,
                                                    newgrounds: validation.error!
                                                });
                                            }
                                        }
                                    }}
                                    validate={validateNewgroundsUrl}
                                    externalError={formErrors.newgrounds}
                                />
                            </div>
                            <div className="min-w-0 flex-1 basis-0">
                                <SocialMediaInput
                                    icon={<GlobeIcon />}
                                    value={profileCopy?.personalUrl || ''}
                                    placeholder="Custom website link"
                                    onChange={(val) => {
                                        clearFieldError('personalUrl');
                                        updateLocalState({
                                            personalUrl: val
                                        });
                                    }}
                                    onBlur={(val) => {
                                        if (typeof val === 'string') {
                                            const personalUrl = val.trim();

                                            // If empty, clear the field
                                            if (!personalUrl) {
                                                updateLocalState({ personalUrl: '' });
                                                handleInputChange({ personalUrl: '' });
                                                return;
                                            }

                                            // Check if it's a valid URL or a domain name
                                            let normalizedUrl = personalUrl;
                                            if (!personalUrl.startsWith('http://') && !personalUrl.startsWith('https://')) {
                                                // Check if it looks like a domain name
                                                if (personalUrl.includes('.') && !personalUrl.includes(' ')) {
                                                    normalizedUrl = `https://${personalUrl}`;
                                                } else {
                                                    setFormErrors({
                                                        ...formErrors,
                                                        personalUrl: 'Please enter a valid website URL'
                                                    });
                                                    return;
                                                }
                                            }

                                            // Validate the URL
                                            try {
                                                new URL(normalizedUrl);
                                                updateLocalState({ personalUrl: normalizedUrl });
                                                handleInputChange({ personalUrl: normalizedUrl });
                                            } catch {
                                                setFormErrors({
                                                    ...formErrors,
                                                    personalUrl: 'Please enter a valid website URL'
                                                });
                                            }
                                        }
                                    }}
                                    validate={(val) => {
                                        if (!val) return { isValid: true };

                                        try {
                                            // If it's not a full URL, try adding https://
                                            let urlToValidate = val;
                                            if (!val.startsWith('http://') && !val.startsWith('https://')) {
                                                if (val.includes('.') && !val.includes(' ')) {
                                                    urlToValidate = `https://${val}`;
                                                } else {
                                                    return { isValid: false, error: 'Please enter a valid website URL' };
                                                }
                                            }

                                            new URL(urlToValidate);
                                            return { isValid: true };
                                        } catch {
                                            return { isValid: false, error: 'Please enter a valid website URL' };
                                        }
                                    }}
                                    externalError={formErrors.personalUrl}
                                />
                            </div>
                        </div>
                    </div>

                    {formErrors.submit && (
                        <div className="text-red-500 text-sm mt-2 text-center shrink-0" role="alert">
                            {formErrors.submit}
                        </div>
                    )}

                    <div className="shrink-0 border-t border-gray-100 bg-transparent px-1 pt-3">
                        <JWButton
                            type="button"
                            onClick={handleSaveProfile}
                            disabled={isSubmitting || hasBlockingFieldErrors}
                            className={`${PRIMARY_BUTTON_CLASS} !shadow-none`}
                        >
                            {isSubmitting ? 'Saving...' : 'Save profile'}
                        </JWButton>
                    </div>
                </FormGroup>
            </JWModal>

            {/* Confirmation Dialog */}
            <JWModal
                show={showConfirmDialog}
                onClose={handleCancelClose}
                setShow={setShowConfirmDialog}
                wrapperClassName={'md:w-[400px] w-full'}
                title="Unsaved Changes"
            >
                <div className="p-3">
                    <p className="mb-3 text-sm">You have unsaved changes. Would you like to save them?</p>
                    <div className="flex justify-end gap-2">
                        <JWButton
                            onClick={handleConfirmClose}
                            className="flex justify-center items-center font-nunito transition-all duration-200 rounded-md text-white text-sm bg-gray-500 px-3 h-8"
                        >
                            Discard Changes
                        </JWButton>
                        <JWButton
                            onClick={handleSaveAndClose}
                            disabled={isSubmitting || hasBlockingFieldErrors}
                            className="flex justify-center items-center font-nunito transition-all duration-200 rounded-md text-white text-sm bg-purple px-3 h-8"
                        >
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </JWButton>
                    </div>
                </div>
            </JWModal>
        </>
    );
}

export default EditProfileModal;
